import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import QuoteForm from "@/components/QuoteForm";
import { SERVICES, SERVICE_AREAS } from "@/lib/api";
import { useRegion, priceFor } from "@/contexts/RegionContext";
import { CheckCircle2, MapPin, Phone } from "lucide-react";

const ServiceArea = () => {
  const { slug } = useParams();
  const { region } = useRegion();
  const area = SERVICE_AREAS.find((a) => a.slug === slug);
  if (!area) return <Navigate to="/" replace />;

  const kicker = area.featured ? "Featured Service Area" : "Service Area";

  return (
    <div data-testid={`area-page-${slug}`}>
      <section className="border-b border-border bg-[hsl(145,27%,24%)] text-white">
        <div className="container-wide py-16 lg:py-20">
          <p className="kicker text-primary">{kicker}</p>
          <h1 className="mt-3 font-display text-4xl font-black sm:text-5xl lg:text-6xl">
            Trash Hauling in {area.name}, FL
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-white/80">
            Fast, affordable trash hauling and junk removal for homeowners and property managers in {area.name},
            {" "}{area.county}. Whether it&apos;s storm debris, an eviction cleanout, or an old fridge in the garage,
            our local crews handle it start to finish — upfront pricing, no surprise fees.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link to="/contact" className="btn-cta" data-testid={`area-cta-${slug}`}>
              Get a Free {area.name} Quote
            </Link>
            <a href="tel:+18135550100" className="btn-ghost-dark">
              <Phone className="h-4 w-4" /> Call (813) 555-0100
            </a>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-wide grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3 space-y-8">
            <div>
              <h2 className="font-display text-3xl font-black sm:text-4xl">
                What we haul in {area.name}
              </h2>
              <p className="mt-3 text-muted-foreground">
                From single-appliance pickups to full property cleanouts, we cover every kind of hauling job in
                {" "}{area.name} and across {area.county}.
              </p>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/services#${s.slug}`} className="flex items-start gap-3 rounded-sm border border-border bg-card p-4 hover:border-primary">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[hsl(145,27%,34%)]" />
                    <div>
                      <p className="font-semibold">{s.title}</p>
                      <p className="text-sm text-muted-foreground">From ${priceFor(s, region)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-sm border border-border bg-muted p-6">
              <p className="kicker">Local SEO</p>
              <h3 className="mt-2 font-display text-xl font-bold">
                {area.name} residents search for us as:
              </h3>
              <p className="mt-3 text-sm text-muted-foreground">
                junk removal {area.name}, trash hauling near me, {area.name} appliance disposal, storm debris pickup {area.name}, eviction cleanout {area.name}, dumpster rental {area.name}, yard waste removal {area.name} FL.
              </p>
            </div>

            <div>
              <h3 className="font-display text-2xl font-bold">Nearby areas we also serve</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {SERVICE_AREAS.filter((a) => a.slug !== slug).map((a) => (
                  <Link key={a.slug} to={`/service-areas/${a.slug}`} className="rounded-sm border border-border bg-white px-3 py-1.5 text-sm hover:border-primary hover:text-primary">
                    <MapPin className="mr-1 inline h-3 w-3" />
                    {a.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <QuoteForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceArea;
